import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  AlertColor,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import TimerIcon from '@mui/icons-material/Timer';
import { createTimestamp } from '../../../utils/firebaseUtils';
import { surgicalCaseService } from '../../../services/surgicalCaseService';
import { surgicalPhasesService } from '../../../services/surgicalPhasesService';
import { useUserData } from '../../../hooks/useUserData';

interface SurgicalProcedureData {
  id: string;
  procedureType: string;
  specialty: string;
  status: string;
  orderDate: Date;
  estimatedDuration?: number;
  requiresConsent?: boolean;
  specialInstructions?: string;
  orderingProvider?: string;
  priority?: string;
}

interface Patient {
  id: string;
  name: string;
  mrn?: string;
}

interface SurgicalIntraOpPhaseProps {
  surgicalProcedure: SurgicalProcedureData;
  patient: Patient;
  encounterId?: string;
}

const SurgicalIntraOpPhase: React.FC<SurgicalIntraOpPhaseProps> = ({ 
  surgicalProcedure, 
  patient, 
  encounterId 
}) => {
  const { user } = useUserData();
  const [checklistStatus, setChecklistStatus] = useState({
    surgicalTimeout: 'pending',
    anesthesiaInduction: 'pending',
    nursingRecord: 'pending',
    specimenTracking: 'pending',
    implantsTracking: 'pending',
    operativeNote: 'pending'
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [loading, setLoading] = useState(false);
  const [intraOpStarted, setIntraOpStarted] = useState(false);
  
  // Dialog form states
  const [timeoutNotes, setTimeoutNotes] = useState('');
  const [anesthesiaStartTime, setAnesthesiaStartTime] = useState('');
  const [anesthesiaNotes, setAnesthesiaNotes] = useState('');
  const [flowsheetData, setFlowsheetData] = useState('');
  const [specimens, setSpecimens] = useState<Array<{id: string, type: string, location: string, notes: string}>>([]);
  const [implants, setImplants] = useState<Array<{id: string, name: string, lotNumber: string, serialNumber: string}>>([]);
  const [operativeNoteContent, setOperativeNoteContent] = useState('');
  const [surgeryStartTime, setSurgeryStartTime] = useState(new Date());

  const showSnackbar = (message: string, severity: AlertColor = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handler functions
  const handleStartIntraOp = async () => {
    try {
      setLoading(true);
      setSurgeryStartTime(new Date());
      
      // Update surgical case status
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'intraOp', {
          status: 'in-progress',
          startTime: createTimestamp(),
          startedBy: user?.uid
        });
      }

      // Prepare session data
      const sessionData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        phase: 'intra_operative'
      };

      // Use service to start intra-op session
      await surgicalPhasesService.startIntraOpSession(sessionData);

      setIntraOpStarted(true);
      showSnackbar('Intra-operative phase started successfully');
    } catch (error) {
      console.error('Error starting intra-op phase:', error);
      showSnackbar('Failed to start intra-op phase', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSurgicalTimeout = () => {
    setOpenDialog('timeout');
  };

  const handleSaveTimeout = async () => {
    if (!timeoutNotes.trim()) {
      showSnackbar('Please enter timeout verification notes', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare timeout data
      const timeoutData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        timeoutNotes: timeoutNotes,
        verificationItems: [
          'Patient identity verified',
          'Procedure and site verified',
          'Surgeon, anesthesiologist, and nurse agreement',
          'Antibiotic prophylaxis confirmed',
          'Equipment and supplies verified'
        ]
      };

      // Use service to create surgical timeout
      await surgicalPhasesService.createSurgicalTimeout(timeoutData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, surgicalTimeout: 'completed' }));
      setOpenDialog(null);
      setTimeoutNotes('');
      showSnackbar('Surgical time-out completed and documented');
    } catch (error) {
      console.error('Error saving timeout:', error);
      showSnackbar('Failed to save timeout documentation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnesthesiaInduction = () => {
    setAnesthesiaStartTime(new Date().toLocaleTimeString());
    setOpenDialog('anesthesia');
  };

  const handleSaveAnesthesia = async () => {
    if (!anesthesiaNotes.trim()) {
      showSnackbar('Please enter anesthesia documentation', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare anesthesia data
      const anesthesiaData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        inductionStartTime: anesthesiaStartTime,
        inductionNotes: anesthesiaNotes,
        anesthesiologist: user?.name || 'Unknown',
        type: 'induction'
      };

      // Use service to create anesthesia record
      await surgicalPhasesService.createAnesthesiaRecord(anesthesiaData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, anesthesiaInduction: 'completed' }));
      setOpenDialog(null);
      setAnesthesiaNotes('');
      showSnackbar('Anesthesia induction documented');
    } catch (error) {
      console.error('Error saving anesthesia record:', error);
      showSnackbar('Failed to save anesthesia documentation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFlowsheet = () => {
    setOpenDialog('flowsheet');
  };

  const handleSaveFlowsheet = async () => {
    if (!flowsheetData.trim()) {
      showSnackbar('Please enter nursing flowsheet data', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare flowsheet data
      const flowsheetRecordData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        flowsheetData: flowsheetData,
        recordedBy: user?.name || 'Unknown Nurse',
        recordType: 'intra_op_flowsheet'
      };

      // Use service to create nursing flowsheet
      await surgicalPhasesService.createNursingFlowsheet(flowsheetRecordData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, nursingRecord: 'completed' }));
      setOpenDialog(null);
      setFlowsheetData('');
      showSnackbar('Nursing flowsheet updated');
    } catch (error) {
      console.error('Error saving flowsheet:', error);
      showSnackbar('Failed to save flowsheet data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentSpecimens = () => {
    setOpenDialog('specimens');
  };

  const handleAddSpecimen = () => {
    const newSpecimen = {
      id: Date.now().toString(),
      type: '',
      location: '',
      notes: ''
    };
    setSpecimens(prev => [...prev, newSpecimen]);
  };

  const handleRemoveSpecimen = (id: string) => {
    setSpecimens(prev => prev.filter(s => s.id !== id));
  };

  const handleSaveSpecimens = async () => {
    if (specimens.length === 0) {
      showSnackbar('Please add at least one specimen', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare specimen data
      const specimenData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        specimens: specimens,
        documentedBy: user?.name || 'Unknown'
      };

      // Use service to create specimen tracking
      await surgicalPhasesService.createSpecimenTracking(specimenData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, specimenTracking: 'completed' }));
      setOpenDialog(null);
      showSnackbar(`${specimens.length} specimen(s) documented successfully`);
    } catch (error) {
      console.error('Error saving specimens:', error);
      showSnackbar('Failed to save specimen documentation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddImplants = () => {
    setOpenDialog('implants');
  };

  const handleAddImplant = () => {
    const newImplant = {
      id: Date.now().toString(),
      name: '',
      lotNumber: '',
      serialNumber: ''
    };
    setImplants(prev => [...prev, newImplant]);
  };

  const handleRemoveImplant = (id: string) => {
    setImplants(prev => prev.filter(i => i.id !== id));
  };

  const handleSaveImplants = async () => {
    if (implants.length === 0) {
      showSnackbar('Please add at least one implant or supply item', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare implant data
      const implantData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        implants: implants,
        documentedBy: user?.name || 'Unknown'
      };

      // Use service to create implant tracking
      await surgicalPhasesService.createImplantTracking(implantData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, implantsTracking: 'completed' }));
      setOpenDialog(null);
      showSnackbar(`${implants.length} implant(s)/supply item(s) documented`);
    } catch (error) {
      console.error('Error saving implants:', error);
      showSnackbar('Failed to save implant documentation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOperativeNote = () => {
    setOpenDialog('operativeNote');
  };

  const handleSaveOperativeNote = async () => {
    if (!operativeNoteContent.trim()) {
      showSnackbar('Please enter operative note content', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare operative note data
      const noteData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        noteContent: operativeNoteContent,
        surgeon: user?.name || 'Unknown Surgeon',
        noteType: 'intra_operative'
      };

      // Use service to create operative note
      await surgicalPhasesService.createOperativeNote(noteData);

      // Update checklist status
      setChecklistStatus(prev => ({ ...prev, operativeNote: 'completed' }));
      setOpenDialog(null);
      setOperativeNoteContent('');
      showSnackbar('Operative note created successfully');
    } catch (error) {
      console.error('Error saving operative note:', error);
      showSnackbar('Failed to save operative note', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintORLabels = async () => {
    try {
      setLoading(true);

      // Prepare print request data
      const printData = {
        documentType: 'or_labels',
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null
      };

      // Use service to create print request
      await surgicalPhasesService.createPrintRequest(printData);

      showSnackbar('OR labels sent to printer successfully');
    } catch (error) {
      console.error('Error printing OR labels:', error);
      showSnackbar('Failed to send labels to printer', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSurgery = async () => {
    const incomplete = Object.entries(checklistStatus).filter(([_, status]) => status !== 'completed');
    if (incomplete.length > 0) {
      showSnackbar('Please complete all checklist items before ending surgery', 'error');
      return;
    }

    try {
      setLoading(true);

      // Update surgical case status
      if (encounterId) {
        const caseId = `case_${surgicalProcedure.id}`;
        await surgicalCaseService.updateCasePhase(caseId, 'intraOp', {
          status: 'completed',
          endTime: createTimestamp(),
          completedBy: user?.uid
        });

        await surgicalCaseService.updateCaseStatus(caseId, 'completed_surgery', 'pacu');
      }

      // Prepare completion data
      const completionData = {
        surgicalProcedureId: surgicalProcedure.id,
        patientId: patient.id,
        encounterId: encounterId || null,
        startTime: surgeryStartTime,
        endTime: new Date(),
        checklistItems: checklistStatus,
        notes: 'Surgery completed successfully - Patient ready for PACU'
      };

      // Use service to create surgery completion
      await surgicalPhasesService.createSurgeryCompletion(completionData);

      showSnackbar('Surgery completed successfully - Patient ready for PACU transfer');
    } catch (error) {
      console.error('Error completing surgery:', error);
      showSnackbar('Failed to complete surgery', 'error');
    } finally {
      setLoading(false);
    }
  };

  const statusIcon = (status: string) => {
    if (status === 'completed') {
      return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32, mr: 2 }} />;
    }
    return <CancelIcon sx={{ color: 'error.main', fontSize: 32, mr: 2 }} />;
  };

  const checklist = [
    {
      key: 'surgicalTimeout',
      title: 'Surgical Time-Out',
      status: checklistStatus.surgicalTimeout,
      description: 'Complete universal protocol verification',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleSurgicalTimeout}
          disabled={loading}
        >
          Complete Time-Out
        </Button>
      )
    },
    {
      key: 'anesthesiaInduction',
      title: 'Anesthesia Induction',
      status: checklistStatus.anesthesiaInduction,
      description: 'Document anesthesia start and monitoring',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleAnesthesiaInduction}
          disabled={loading}
        >
          Document Start
        </Button>
      )
    },
    {
      key: 'nursingRecord',
      title: 'Intra-Op Nursing Record',
      status: checklistStatus.nursingRecord,
      description: 'Update flowsheet and monitoring data',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleOpenFlowsheet}
          disabled={loading}
        >
          Open Flowsheet
        </Button>
      )
    },
    {
      key: 'specimenTracking',
      title: 'Specimen Tracking',
      status: checklistStatus.specimenTracking,
      description: 'Document all surgical specimens',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleDocumentSpecimens}
          disabled={loading}
        >
          Document Specimens
        </Button>
      )
    },
    {
      key: 'implantsTracking',
      title: 'Implant & Supplies Tracking',
      status: checklistStatus.implantsTracking,
      description: 'Record implants and supply usage',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleAddImplants}
          disabled={loading}
        >
          Add Implants
        </Button>
      )
    },
    {
      key: 'operativeNote',
      title: 'Surgeon Operative Note',
      status: checklistStatus.operativeNote,
      description: 'Create intra-operative documentation',
      action: (
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={handleCreateOperativeNote}
          disabled={loading}
        >
          Create Note
        </Button>
      )
    }
  ];

  return (
    <Box sx={{ p: 0 }}>
      {/* Status Bar */}
      <Card sx={{ mb: 3, borderRadius: 2, background: '#fafbfc' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Intra-Op Status:
            </Typography>
            <Chip
              label={surgicalProcedure.status === 'completed' ? 'Completed' : 'Not Started'}
              color={surgicalProcedure.status === 'completed' ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 1, fontWeight: 600, fontSize: 14 }}
              icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Procedure: {surgicalProcedure.procedureType}
          </Typography>
        </CardContent>
      </Card>

      {/* Procedure Details */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Procedure Details
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Procedure Type:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{surgicalProcedure.procedureType}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">Specialty:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{surgicalProcedure.specialty}</Typography>
            </Box>
            {surgicalProcedure.estimatedDuration && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Estimated Duration:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {surgicalProcedure.estimatedDuration} minutes
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Intra-Op Checklist */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Intra-Op Checklist
      </Typography>
      <Stack spacing={2}>
        {checklist.map(item => (
          <Card key={item.key} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon(item.status)}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.status === 'completed' ? 'Completed' : item.description}
                  </Typography>
                </Box>
              </Box>
              <Box>
                {item.action}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Sticky Footer Actions */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        width: '100%',
        background: '#fafbfc',
        borderTop: '1px solid #e0e0e0',
        mt: 4,
        py: 2,
        zIndex: 10
      }}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Button 
            variant="contained" 
            color="success" 
            sx={{ minWidth: 180 }}
            onClick={handleStartIntraOp}
            disabled={loading || intraOpStarted}
            startIcon={<TimerIcon />}
          >
            {intraOpStarted ? 'Phase Started' : 'Start Intra-Op Phase'}
          </Button>
          <Button 
            variant="contained" 
            color="inherit" 
            sx={{ minWidth: 180 }}
            onClick={handlePrintORLabels}
            disabled={loading}
          >
            Print OR Labels
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ minWidth: 140 }}
            onClick={handleCompleteSurgery}
            disabled={loading}
          >
            Complete Surgery
          </Button>
        </Stack>
      </Box>

      {/* Timeout Dialog */}
      <Dialog open={openDialog === 'timeout'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Complete Surgical Time-Out</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Time-Out Verification Notes"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={timeoutNotes}
            onChange={(e) => setTimeoutNotes(e.target.value)}
            placeholder="Document verification of patient identity, procedure, site, surgeon/anesthesiologist agreement, antibiotic prophylaxis, equipment readiness..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveTimeout} variant="contained" disabled={loading}>
            Complete Time-Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Anesthesia Dialog */}
      <Dialog open={openDialog === 'anesthesia'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Document Anesthesia Induction</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Induction Start Time"
              value={anesthesiaStartTime}
              onChange={(e) => setAnesthesiaStartTime(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Anesthesia Notes"
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              value={anesthesiaNotes}
              onChange={(e) => setAnesthesiaNotes(e.target.value)}
              placeholder="Document anesthesia type, medications administered, monitoring parameters, patient response..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveAnesthesia} variant="contained" disabled={loading}>
            Save Anesthesia Record
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flowsheet Dialog */}
      <Dialog open={openDialog === 'flowsheet'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Update Intra-Op Nursing Flowsheet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Flowsheet Data"
            fullWidth
            multiline
            rows={8}
            variant="outlined"
            value={flowsheetData}
            onChange={(e) => setFlowsheetData(e.target.value)}
            placeholder="Document vital signs, fluid balance, positioning, skin integrity, equipment settings, patient response..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveFlowsheet} variant="contained" disabled={loading}>
            Update Flowsheet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Specimens Dialog */}
      <Dialog open={openDialog === 'specimens'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Document Surgical Specimens</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddSpecimen}
              sx={{ mb: 2 }}
            >
              Add Specimen
            </Button>
          </Box>
          <List>
            {specimens.map((specimen, index) => (
              <ListItem key={specimen.id} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Specimen Type"
                      value={specimen.type}
                      onChange={(e) => {
                        const newSpecimens = [...specimens];
                        newSpecimens[index].type = e.target.value;
                        setSpecimens(newSpecimens);
                      }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Anatomical Location"
                      value={specimen.location}
                      onChange={(e) => {
                        const newSpecimens = [...specimens];
                        newSpecimens[index].location = e.target.value;
                        setSpecimens(newSpecimens);
                      }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Notes"
                      value={specimen.notes}
                      onChange={(e) => {
                        const newSpecimens = [...specimens];
                        newSpecimens[index].notes = e.target.value;
                        setSpecimens(newSpecimens);
                      }}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                    />
                  </Stack>
                </Box>
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => handleRemoveSpecimen(specimen.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveSpecimens} variant="contained" disabled={loading}>
            Save Specimens
          </Button>
        </DialogActions>
      </Dialog>

      {/* Implants Dialog */}
      <Dialog open={openDialog === 'implants'} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Add Implants & Supplies</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddImplant}
              sx={{ mb: 2 }}
            >
              Add Implant/Supply
            </Button>
          </Box>
          <List>
            {implants.map((implant, index) => (
              <ListItem key={implant.id} sx={{ border: '1px solid #e0e0e0', mb: 1, borderRadius: 1 }}>
                <Box sx={{ width: '100%' }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Implant/Supply Name"
                      value={implant.name}
                      onChange={(e) => {
                        const newImplants = [...implants];
                        newImplants[index].name = e.target.value;
                        setImplants(newImplants);
                      }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Lot Number"
                      value={implant.lotNumber}
                      onChange={(e) => {
                        const newImplants = [...implants];
                        newImplants[index].lotNumber = e.target.value;
                        setImplants(newImplants);
                      }}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Serial Number"
                      value={implant.serialNumber}
                      onChange={(e) => {
                        const newImplants = [...implants];
                        newImplants[index].serialNumber = e.target.value;
                        setImplants(newImplants);
                      }}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                </Box>
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => handleRemoveImplant(implant.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveImplants} variant="contained" disabled={loading}>
            Save Implants/Supplies
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operative Note Dialog */}
      <Dialog open={openDialog === 'operativeNote'} onClose={() => setOpenDialog(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Create Surgeon Operative Note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Operative Note"
            fullWidth
            multiline
            rows={12}
            variant="outlined"
            value={operativeNoteContent}
            onChange={(e) => setOperativeNoteContent(e.target.value)}
            placeholder="Document surgical approach, findings, procedures performed, complications (if any), specimens obtained, closure technique, patient condition..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(null)} disabled={loading}>Cancel</Button>
          <Button onClick={handleSaveOperativeNote} variant="contained" disabled={loading}>
            Save Operative Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurgicalIntraOpPhase; 